import { useState, useRef } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Textarea, Select } from '@/components/ui'
import { FileText, Download, Plus, Trash2, MoveUp, MoveDown, Image } from 'lucide-react'
import { toast } from 'sonner'
import jsPDF from 'jspdf'

interface PDFElement {
  id: string
  type: 'heading' | 'paragraph' | 'image' | 'divider' | 'list'
  content: string
  style?: {
    fontSize?: number
    bold?: boolean
    italic?: boolean
    align?: 'left' | 'center' | 'right'
  }
}

export function PDFStudio() {
  const [title, setTitle] = useState('My Document')
  const [elements, setElements] = useState<PDFElement[]>([
    { id: '1', type: 'heading', content: 'Welcome to PDF Studio', style: { fontSize: 24, bold: true } },
    { id: '2', type: 'paragraph', content: 'This is a sample paragraph. You can edit this text and add more elements below.' },
  ])
  const [pageSize, setPageSize] = useState<'a4' | 'letter'>('a4')
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addElement = (type: PDFElement['type']) => {
    const newElement: PDFElement = {
      id: crypto.randomUUID(),
      type,
      content: type === 'heading' ? 'New Heading' : type === 'paragraph' ? 'New paragraph text...' : type === 'list' ? '• Item 1\n• Item 2\n• Item 3' : '',
      style: type === 'heading' ? { fontSize: 18, bold: true } : { fontSize: 12 },
    }
    setElements([...elements, newElement])
  }

  const updateElement = (id: string, updates: Partial<PDFElement>) => {
    setElements(elements.map((el) => (el.id === id ? { ...el, ...updates } : el)))
  }

  const removeElement = (id: string) => {
    setElements(elements.filter((el) => el.id !== id))
  }

  const moveElement = (id: string, direction: 'up' | 'down') => {
    const index = elements.findIndex((el) => el.id === id)
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === elements.length - 1)) return

    const newElements = [...elements]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[newElements[index], newElements[swapIndex]] = [newElements[swapIndex], newElements[index]]
    setElements(newElements)
  }

  const generatePDF = () => {
    const pdf = new jsPDF({
      orientation,
      unit: 'mm',
      format: pageSize,
    })

    const pageWidth = pdf.internal.pageSize.getWidth()
    const margin = 20
    let y = margin

    // Title
    pdf.setFontSize(24)
    pdf.setFont('helvetica', 'bold')
    pdf.text(title, pageWidth / 2, y, { align: 'center' })
    y += 15

    // Elements
    elements.forEach((element) => {
      const fontSize = element.style?.fontSize || 12
      pdf.setFontSize(fontSize)
      pdf.setFont('helvetica', element.style?.bold ? 'bold' : 'normal')

      if (y > pdf.internal.pageSize.getHeight() - margin) {
        pdf.addPage()
        y = margin
      }

      switch (element.type) {
        case 'heading':
          pdf.text(element.content, margin, y)
          y += fontSize * 0.5 + 5
          break

        case 'paragraph':
          const lines = pdf.splitTextToSize(element.content, pageWidth - margin * 2)
          pdf.text(lines, margin, y)
          y += lines.length * fontSize * 0.4 + 8
          break

        case 'list':
          const items = element.content.split('\n')
          items.forEach((item) => {
            pdf.text(item, margin + 5, y)
            y += fontSize * 0.4 + 2
          })
          y += 5
          break

        case 'divider':
          pdf.setDrawColor(200)
          pdf.line(margin, y, pageWidth - margin, y)
          y += 10
          break
      }
    })

    // Footer
    const pageCount = pdf.internal.pages.length - 1
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i)
      pdf.setFontSize(10)
      pdf.setFont('helvetica', 'normal')
      pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pdf.internal.pageSize.getHeight() - 10, { align: 'center' })
    }

    pdf.save(`${title.replace(/[^a-z0-9]/gi, '_')}.pdf`)
    toast.success('PDF downloaded!')
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">PDF Studio</h1>
            <p className="text-muted-foreground">Create and download PDF documents</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_350px]">
        {/* Editor */}
        <div className="space-y-4">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Document Editor</CardTitle>
              <CardDescription>Build your PDF by adding elements</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Title */}
              <div className="space-y-2">
                <Label>Document Title</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter document title"
                />
              </div>

              {/* Elements */}
              <div className="space-y-3">
                {elements.map((element, index) => (
                  <div key={element.id} className="p-4 border border-border rounded-lg bg-muted/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground uppercase">
                        {element.type}
                      </span>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveElement(element.id, 'up')}
                          disabled={index === 0}
                        >
                          <MoveUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveElement(element.id, 'down')}
                          disabled={index === elements.length - 1}
                        >
                          <MoveDown className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive"
                          onClick={() => removeElement(element.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    {element.type === 'divider' ? (
                      <div className="h-px bg-border my-2" />
                    ) : (
                      <Textarea
                        value={element.content}
                        onChange={(e) => updateElement(element.id, { content: e.target.value })}
                        className="min-h-[60px]"
                        placeholder={`Enter ${element.type} content...`}
                      />
                    )}

                    {element.type !== 'divider' && (
                      <div className="flex gap-2 mt-2">
                        <Select
                          value={String(element.style?.fontSize || 12)}
                          onChange={(e) =>
                            updateElement(element.id, {
                              style: { ...element.style, fontSize: Number(e.target.value) },
                            })
                          }
                          className="w-24"
                        >
                          <option value="10">10pt</option>
                          <option value="12">12pt</option>
                          <option value="14">14pt</option>
                          <option value="18">18pt</option>
                          <option value="24">24pt</option>
                        </Select>
                        <Button
                          variant={element.style?.bold ? 'default' : 'outline'}
                          size="sm"
                          onClick={() =>
                            updateElement(element.id, {
                              style: { ...element.style, bold: !element.style?.bold },
                            })
                          }
                        >
                          B
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Element Buttons */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                <Button variant="outline" size="sm" onClick={() => addElement('heading')}>
                  <Plus className="h-3 w-3 mr-1" /> Heading
                </Button>
                <Button variant="outline" size="sm" onClick={() => addElement('paragraph')}>
                  <Plus className="h-3 w-3 mr-1" /> Paragraph
                </Button>
                <Button variant="outline" size="sm" onClick={() => addElement('list')}>
                  <Plus className="h-3 w-3 mr-1" /> List
                </Button>
                <Button variant="outline" size="sm" onClick={() => addElement('divider')}>
                  <Plus className="h-3 w-3 mr-1" /> Divider
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Settings & Preview */}
        <div className="space-y-4">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Page Size</Label>
                <Select value={pageSize} onChange={(e) => setPageSize(e.target.value as 'a4' | 'letter')}>
                  <option value="a4">A4</option>
                  <option value="letter">Letter</option>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Orientation</Label>
                <Select value={orientation} onChange={(e) => setOrientation(e.target.value as 'portrait' | 'landscape')}>
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </Select>
              </div>

              <Button onClick={generatePDF} className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader>
              <CardTitle>Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="aspect-[1/1.4] bg-white rounded-lg p-4 text-black overflow-hidden">
                <h1 className="text-center font-bold text-sm mb-3">{title}</h1>
                <div className="space-y-2 text-[8px] leading-tight">
                  {elements.slice(0, 5).map((el) => (
                    <div key={el.id}>
                      {el.type === 'divider' ? (
                        <hr className="border-gray-300" />
                      ) : (
                        <p className={el.style?.bold ? 'font-bold' : ''}>
                          {el.content.substring(0, 100)}
                          {el.content.length > 100 ? '...' : ''}
                        </p>
                      )}
                    </div>
                  ))}
                  {elements.length > 5 && (
                    <p className="text-gray-400">+{elements.length - 5} more elements...</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
